import classNames from "classnames";
import clamp from "lodash/clamp";
import React, { FunctionComponent, useCallback, useMemo, useState } from "react";
import { useIntl } from "react-intl";
import ReactResizeDetector from "react-resize-detector";
import { v1 as uuid } from "uuid";
import useCustomizationContext from "../../../../hooks/useCustomizationContext";
import { ReactComponent as IconArrowLeft } from "../../../../icons/arrow-left.svg";
import { ReactComponent as IconArrowRight } from "../../../../icons/arrow-right.svg";
import { ReactComponent as IconImage } from "../../../../icons/image.svg";
import { ReactComponent as IconTrash } from "../../../../icons/trash.svg";
import { ReactComponent as IconZap } from "../../../../icons/zap.svg";
import { EditedPicture, Panel } from "../../../../states/customization";
import { Cropping } from "../../../../types/customization/content";
import { sizeDmmToMm, sizeMmToPx } from "../../../../utils/customization/box";
import { isMobileSize } from "../../../../utils/customization/device";
import {
  findEditablePictureDefinition,
  findPageAndDefinition,
} from "../../../../utils/customization/finders";
import ConfirmModal from "../../../molecules/ConfirmModal";
import EditablePicture from "../../../molecules/customization/EditablePicture";
import EditorsValidationHeader from "../../../molecules/customization/EditorsValidationHeader";
import FilterPanel from "../panels/FilterPanel";
import PictureEditor from "../PictureEditor";
import "./style.scss";

const DEFAULT_PPMM = 5;
// keep space around + buttons :
const MARGIN_WIDTH = 50 + (!isMobileSize() ? 240 : 0);
const RESPONSIVE_SPACES = !isMobileSize() ? 128 : 36;
// space around + header modale + header editor + buttons under picture
const MARGIN_HEIGHT = RESPONSIVE_SPACES + 64 + 32 + 80;

interface Props {
  editedPicture: EditedPicture;
}

const PictureEditorView: FunctionComponent<Props> = ({ editedPicture }) => {
  const intl = useIntl();
  const {
    configuration,
    content,
    dispatch,
    selection,
    template,
    ui: { panel },
    undoRedo,
  } = useCustomizationContext();
  const [pictureEditorKey] = useState<string>(uuid());
  const [displayFilters, setDisplayFilters] = useState<boolean>(false);

  const { editablePictureKey, pageKey } = editedPicture;

  const { page, pageDefinition } = useMemo(
    () => findPageAndDefinition(pageKey, template, content),
    [content, pageKey, template]
  );
  const elementDefinition = useMemo(
    () => findEditablePictureDefinition(editablePictureKey, pageDefinition),
    [editablePictureKey, pageDefinition]
  );
  const elementSizeMm = sizeDmmToMm(elementDefinition);
  const element = useMemo(
    () => page.editablePictures.find((elem) => elem.editablePictureKey === editablePictureKey),
    [editablePictureKey, page.editablePictures]
  );

  const editablePictureDefinitionsList = pageDefinition.elementDefinitions.filter(
    (elemDefinition) => elemDefinition.type === "editable-picture"
  );
  const elementPlaceInList =
    editablePictureDefinitionsList.length > 0
      ? editablePictureDefinitionsList.findIndex(
          (elemDefinition) => elemDefinition.key === elementDefinition.key
        )
      : 0;
  let carouselButtons;
  if (editablePictureDefinitionsList.length > 1) {
    const onClickPreviousHandler = () => {
      if (elementPlaceInList < 1) {
        return;
      }
      save();
      const newEditablePicture = editablePictureDefinitionsList[elementPlaceInList - 1];
      dispatch({
        editedPicture: { editablePictureKey: newEditablePicture.key, pageKey },
        type: "setEditedPicture",
      });
    };
    const onClickNextHandler = () => {
      if (elementPlaceInList > editablePictureDefinitionsList.length - 2) {
        return;
      }
      save();
      const newEditablePicture = editablePictureDefinitionsList[elementPlaceInList + 1];
      dispatch({
        editedPicture: { editablePictureKey: newEditablePicture.key, pageKey },
        type: "setEditedPicture",
      });
    };
    carouselButtons = (
      <>
        <button
          className="PictureEditorView-carousel-button PictureEditorView-carousel-button-previous"
          disabled={elementPlaceInList === 0}
          onClick={onClickPreviousHandler}
          title={intl.formatMessage({
            id: "kustom_LottieCarousel_button_previous",
          })}
          type="button"
        >
          <IconArrowLeft />
        </button>
        <button
          className="PictureEditorView-carousel-button PictureEditorView-carousel-button-next"
          disabled={elementPlaceInList === editablePictureDefinitionsList.length - 1}
          onClick={onClickNextHandler}
          title={intl.formatMessage({
            id: "kustom_LottieCarousel_button_next",
          })}
          type="button"
        >
          <IconArrowRight />
        </button>
      </>
    );
  }

  const [ppmm, setPpmm] = useState<number>(DEFAULT_PPMM);
  const onResize = useCallback(
    (width: number | undefined, height: number | undefined) => {
      if (!width || !height) {
        setPpmm(1);
        return;
      }

      const availableWidth = clamp(width - MARGIN_WIDTH, 0, 800);
      const availableHeight = clamp(height - MARGIN_HEIGHT, 0, 800);

      const ppmmWidth = availableWidth / elementSizeMm.width.value;
      const ppmmHeight = availableHeight / elementSizeMm.height.value;

      const ppmmMin = Math.min(ppmmWidth, ppmmHeight);

      // console.log(availableWidth, availableHeight, ppmmMin);
      setPpmm(clamp(ppmmMin, 0.5, 20));
    },
    [elementSizeMm, setPpmm]
  );

  const elementSizePx = useMemo(() => sizeMmToPx(elementSizeMm, ppmm), [elementSizeMm, ppmm]);
  const mainStyle = useMemo(() => {
    return {
      height: elementSizePx.height.value + 2, // border
      width: elementSizePx.width.value + 2, // border
    };
  }, [elementSizePx]);

  const [cropping, setCropping] = useState<Cropping | null>(null);
  const [photoKey, setPhotoKey] = useState<string | null>(
    (element && element.selectionPhotoKey) || null
  );
  const [editablePictureKeyRef, setEditablePictureKeyRef] = useState<string | null>(
    (element && element.editablePictureKey) || null
  );
  const [croppingModified, setCroppingModified] = useState<boolean>(false);

  const save = useCallback(() => {
    if (cropping === null || element === undefined) {
      return;
    }
    dispatch({
      cropping,
      editablePictureKey,
      filterTag: element.filterTag,
      pageKey,
      type: "changeEditablePictureElementCroppingAndFilter",
      undoKey: `PictureEditorView-${pageKey}`,
    });
  }, [cropping, dispatch, editablePictureKey, element, pageKey]);

  const exit = useCallback(() => {
    dispatch({ type: "setPageToFocus", pageKey });
    dispatch({
      editedPicture: null,
      type: "setEditedPicture",
    });
    if (panel.current !== Panel.CLOSED) {
      dispatch({
        key: pageKey,
        openedTab: panel.openedTab,
        panel: pageDefinition.type === "front-cover" ? Panel.EDITCOVER : Panel.EDITPAGE,
        type: "setPanel",
      });
    }
  }, [dispatch, pageKey]);

  const [initialHistoryCount] = useState<number>(undoRedo.undoStack.length);
  const cancelAndExit = useCallback(() => {
    if (initialHistoryCount < undoRedo.undoStack.length) {
      dispatch({ type: "undoRedo", way: "undo" });
    }
    exit();
  }, [dispatch, exit, initialHistoryCount, undoRedo.undoStack.length]);

  const saveAndExit = () => {
    save();
    exit();
  };

  const [displayConfirmModal, setDisplayConfirmModal] = useState<boolean>(false);
  const onClickCancelHandler = useCallback(
    (event: MouseEvent) => {
      event.preventDefault();
      if (initialHistoryCount < undoRedo.undoStack.length || croppingModified) {
        setDisplayConfirmModal(true);
      } else {
        exit();
      }
    },
    [croppingModified, exit, initialHistoryCount, setDisplayConfirmModal, undoRedo.undoStack.length]
  );

  const onClickSaveHandler = (event: MouseEvent) => {
    event.preventDefault();
    saveAndExit();
  };

  const onChangeCroppingHandler = useCallback(
    (newCropping: Cropping) => {
      // check to update values here
      setCroppingModified(true);
      setCropping(newCropping);
    },
    [setCropping, setCroppingModified]
  );

  const handleClickOnRemovePictureButton = () => {
    dispatch({
      editablePictureKey,
      pageKey,
      type: "removePicture",
      undoKey: pictureEditorKey,
    });
    setDisplayFilters(false);
  };

  const handleClickOnFiltersButton = () => {
    setDisplayFilters((displayed) => !displayed);
  };

  const handleClickOnChangePictureButton = useCallback(() => {
    dispatch({
      type: "setGallery",
      open: true,
      pageKey,
      undoKey: pictureEditorKey,
    });
  }, [dispatch, pageKey, pictureEditorKey]);

  const pictureEditorContent = useMemo(() => {
    if (element) {
      if (photoKey !== element.selectionPhotoKey || editablePictureKeyRef !== editablePictureKey) {
        setCropping(element.cropping);
        setPhotoKey(element.selectionPhotoKey);
        setEditablePictureKeyRef(editablePictureKey);
      }
      return (
        <PictureEditor
          element={element}
          elementDefinition={elementDefinition}
          elementSizeMm={elementSizeMm}
          onChangeCropping={onChangeCroppingHandler}
          pageDefinition={pageDefinition}
          ppmm={ppmm}
          selection={selection}
          template={template}
          withZoomOut={configuration?.withZoomOut !== undefined ? configuration.withZoomOut : true}
        />
      );
    } else {
      return (
        <div
          className="PictureEditorView-main"
          onClick={handleClickOnChangePictureButton}
          style={mainStyle}
        >
          <EditablePicture
            elementDefinitionKey={elementDefinition.key}
            page={page}
            pageDefinition={pageDefinition}
            ppmm={ppmm}
            template={template}
          />
        </div>
      );
    }
  }, [
    content,
    editablePictureKey,
    editablePictureKeyRef,
    element,
    elementDefinition,
    elementSizeMm,
    mainStyle,
    onChangeCroppingHandler,
    handleClickOnChangePictureButton,
    pageDefinition,
    pageKey,
    photoKey,
    ppmm,
    selection,
    template,
  ]);

  let pictureChoiceButtonLabel: string;
  if (element) {
    pictureChoiceButtonLabel = intl.formatMessage({
      id: "kustom_PictureEditor_replace",
    });
  } else {
    pictureChoiceButtonLabel = intl.formatMessage({
      id: "kustom_PictureEditor_choose",
    });
  }

  return (
    <div className="PictureEditorView">
      <ReactResizeDetector handleHeight={true} handleWidth={true} onResize={onResize} />
      <div className="PictureEditorView-overlay" />

      <ConfirmModal
        confirmLabel={intl.formatMessage({
          id: "kustom_PictureEditorView_modal_confirm",
        })}
        display={displayConfirmModal}
        onConfirm={cancelAndExit}
        onDismiss={() => setDisplayConfirmModal(false)}
        validationButtonType="warning"
      >
        <h2 className="PictureEditorView-modal-title">
          {intl.formatMessage({ id: "kustom_PictureEditorView_modal_title" })}
        </h2>
        <p className="PictureEditorView-modal-text">
          {intl.formatMessage({ id: "kustom_PictureEditorView_modal_text" })}
        </p>
      </ConfirmModal>
      <EditorsValidationHeader
        elementPlaceDetails={`${elementPlaceInList + 1}/${editablePictureDefinitionsList.length}`}
        onCancel={onClickCancelHandler}
        onValidate={onClickSaveHandler}
        pageKey={pageKey}
      />

      <div className="PictureEditorView-layout">
        <div className="PictureEditorView-wrapper">
          <div className="PictureEditorView-wrapper-carousel">
            {pictureEditorContent}

            {carouselButtons}
          </div>

          <div className="PictureEditorView-footer">
            <button
              className="PictureEditorView-footer-button"
              disabled={!element}
              onClick={handleClickOnRemovePictureButton}
              title={intl.formatMessage({ id: "kustom_PictureEditor_remove" })}
              type="button"
            >
              <IconTrash height="28" width="28" />
              {intl.formatMessage({ id: "kustom_PictureEditor_remove" })}
            </button>

            <button
              className="PictureEditorView-footer-button"
              disabled={!element}
              onClick={handleClickOnFiltersButton}
              title={intl.formatMessage({ id: "kustom_PictureEditor_filters" })}
              type="button"
            >
              <IconZap height="28" width="28" />
              {intl.formatMessage({ id: "kustom_PictureEditor_filters" })}
            </button>

            <button
              className="PictureEditorView-footer-button"
              onClick={handleClickOnChangePictureButton}
              title={pictureChoiceButtonLabel}
              type="button"
            >
              <IconImage height="28" width="28" />
              {pictureChoiceButtonLabel}
            </button>
          </div>
        </div>

        <div
          className={classNames("PictureEditorView-layout-panel", {
            "PictureEditorView-layout-panel--closed": !displayFilters,
          })}
        >
          {element && (
            <FilterPanel editedPicture={editedPicture} onClose={handleClickOnFiltersButton} />
          )}
        </div>
      </div>
    </div>
  );
};

export default PictureEditorView;
